import React, { useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { getToolBySlug } from '@/data/tools';
import NavBar from '@/components/NavBar';
import SEOHead from '@/components/SEOHead';
import BreadcrumbSEO from '@/components/BreadcrumbSEO';
import ImageUploader from '@/components/ImageUploader';
import ToolExample from '@/components/ToolExample';
import RelatedTools from '@/components/RelatedTools';
import NotFound from '@/pages/NotFound';
import { CheckCircle, HelpCircle, Loader } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';

const ImageResizer = lazy(() => import('@/components/ImageResizer'));
const TargetSizeCompressor = lazy(() => import('@/components/TargetSizeCompressor'));
const FormatConverter = lazy(() => import('@/components/FormatConverter'));
const ImageCropper = lazy(() => import('@/components/ImageCropper'));
const ImageRotator = lazy(() => import('@/components/ImageRotator'));
const ImageResizerPercent = lazy(() => import('@/components/ImageResizerPercent'));
const BatchProcessor = lazy(() => import('@/components/BatchProcessor'));
const ImageAdjuster = lazy(() => import('@/components/ImageAdjuster'));
const ImageToPdf = lazy(() => import('@/components/ImageToPdf'));
const AdSlot = lazy(() => import('@/components/AdSlot'));

const ToolPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const tool = slug ? getToolBySlug(slug) : undefined;
  const { trackEvent } = useAnalytics();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (tool) {
      trackEvent('page_view', { page: `/herramientas/${tool.slug}`, page_type: 'tool_page', tool_slug: tool.slug, device_type: isMobile ? 'mobile' : 'desktop' });
    }
  }, [tool?.slug]);

  if (!tool) return <NotFound />;

  // Read metadata directly from tool definition — no slug parsing needed
  const sourceFormat = tool.sourceFormat;
  const targetFormat = tool.targetFormat;
  const targetSize = tool.targetSize;

  // FAQ structured data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": tool.faqItems.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={tool.metaTitle}
        description={tool.metaDescription}
        canonical={`https://compresorjpeg.com${tool.canonicalPath}`}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      <NavBar />

      <main className="container mx-auto px-4 py-6">
        <BreadcrumbSEO
          items={[
            { label: 'Inicio', href: '/' },
            { label: 'Herramientas', href: '/herramientas' },
            { label: tool.title },
          ]}
        />

        <section className="text-center py-8 max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {tool.h1}
          </h1>
          <p className="text-lg text-muted-foreground">
            {tool.subtitle}
          </p>
        </section>

        {/* Top Banner Ad — below header, above tool */}
        <Suspense fallback={null}>
          <AdSlot position="top-banner" slotId="REPLACE_WITH_REAL_ID" className="max-w-3xl mx-auto mb-6" />
        </Suspense>

        <section className="mb-8">
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-primary animate-spin" />
            </div>
          }>
            {(() => {
              switch (tool.pageType) {
                case 'resize':
                  return <ImageResizer tool={tool} />;
                case 'target-size':
                  return <TargetSizeCompressor tool={tool} />;
                case 'conversion':
                  return <FormatConverter tool={tool} />;
                case 'crop':
                  return <ImageCropper tool={tool} />;
                case 'rotate':
                  return <ImageRotator tool={tool} />;
                case 'resize-percent':
                  return <ImageResizerPercent tool={tool} />;
                case 'batch':
                  return <BatchProcessor tool={tool} />;
                case 'adjust':
                  return <ImageAdjuster tool={tool} />;
                case 'image-to-pdf':
                  return <ImageToPdf tool={tool} />;
                default:
                  return <ImageUploader />;
              }
            })()}
          </Suspense>
        </section>

        <section className="max-w-3xl mx-auto mb-8">
          <p className="text-muted-foreground leading-relaxed">
            {tool.introParagraph}
          </p>
        </section>

        <Suspense fallback={null}>
          <AdSlot position="content" slotId="REPLACE_WITH_REAL_ID" className="max-w-3xl mx-auto my-8" />
        </Suspense>

        <section className="mb-16">
          <ToolExample
            toolExampleType={tool.toolExampleType}
            sourceFormat={sourceFormat}
            targetFormat={targetFormat}
            targetSize={targetSize}
          />
        </section>

        {tool.seoIntro && (
          <section className="max-w-3xl mx-auto mb-16">
            <p className="text-muted-foreground leading-relaxed">
              {tool.seoIntro}
            </p>
          </section>
        )}

        <section className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            ¿Cómo funciona?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tool.howItWorksSteps.map(step => (
              <div key={step.number} className="text-center p-6 rounded-xl bg-card border border-border">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Ventajas
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {tool.benefits.map((benefit, i) => (
              <div key={i} className="flex gap-3 p-4 rounded-lg bg-card border border-border">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Preguntas frecuentes
          </h2>
          <div className="space-y-4">
            {tool.faqItems.map((faq, i) => (
              <details key={i} className="group p-4 rounded-lg bg-card border border-border">
                <summary className="flex items-center gap-2 cursor-pointer font-semibold text-foreground list-none">
                  <HelpCircle className="h-4 w-4 text-primary shrink-0" />
                  {faq.question}
                </summary>
                <p className="mt-3 text-sm text-muted-foreground pl-6">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        <RelatedTools currentSlug={tool.slug} relatedSlugs={tool.relatedSlugs} />

        <Suspense fallback={null}>
          <AdSlot position="content" slotId="REPLACE_WITH_REAL_ID" className="max-w-3xl mx-auto my-8" />
        </Suspense>
      </main>

      <footer className="border-t border-border py-8 mt-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} CompresorJPEG — Todas las herramientas de optimización de imágenes online.</p>
        </div>
      </footer>
    </div>
  );
};

export default ToolPage;
